import { APP_NAME, BRAND_LOGO } from "@/lib/brand";
import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FolderKanban,
  CheckSquare,
  Building2,
  Search,
  Bell,
  LogOut,
  Zap,
  ChevronDown,
  Inbox,
  Hash,
  ShoppingCart,
  Users,
  Truck,
  Workflow,
  FileSpreadsheet,
  HardDrive,
  Sun,
  Settings,
  ShieldCheck,
  Archive,
  Menu,
  LifeBuoy,
} from "lucide-react";
import { api, avatarUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { GlobalSearch } from "@/components/GlobalSearch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CreateWorkspaceDialog } from "@/components/workspace/CreateWorkspaceDialog";
import { LegalConsentGate } from "@/components/LegalConsentGate";
import { WorkspaceSetupGate } from "@/components/WorkspaceSetupGate";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { label: "Dashboard", path: "/", icon: LayoutDashboard, end: true },
  { label: "Projects", path: "/projects", icon: FolderKanban },
  { label: "Archive", path: "/archive", icon: Archive },
  { label: "My Day", path: "/my-day", icon: Sun },
  { label: "Inbox", path: "/inbox", icon: Inbox },
  { label: "Order Requests", path: "/orders", icon: ShoppingCart },
];

const ADMIN_NAV = [
  { label: "Users & Roles", path: "/admin/users", icon: Users },
  { label: "Customers & Brands", path: "/admin/customers", icon: Building2 },
  { label: "Suppliers", path: "/admin/suppliers", icon: Truck },
  { label: "Workflow Template", path: "/admin/workflow", icon: Workflow },
  { label: "Economic File", path: "/admin/economic-file", icon: FileSpreadsheet },
  { label: "Project Numbering", path: "/admin/numbering", icon: Hash },
  { label: "Backup & Storage", path: "/admin/backup", icon: HardDrive },
  { label: "Workspace Settings", path: "/admin/settings", icon: Settings },
];

export default function AppLayout() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [workspaces, setWorkspaces] = useState([]);
  const [createWsOpen, setCreateWsOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const refresh = () => api.get("/inbox").then((r) => setUnread(r.data.unread)).catch(() => {});
    refresh();
    const t = setInterval(refresh, 30000);
    window.addEventListener("inbox-changed", refresh);
    return () => { clearInterval(t); window.removeEventListener("inbox-changed", refresh); };
  }, []);

  useEffect(() => { api.get("/workspaces").then((r) => setWorkspaces(r.data.workspaces || [])).catch(() => {}); }, [user?.company_id, user?.workspace_setup_needed]);

  const switchWorkspace = async (companyId) => {
    if (companyId === user?.company_id) return;
    await api.post("/workspaces/switch", { company_id: companyId });
    await checkAuth();
    navigate("/", { replace: true });
    window.location.reload();
  };

  const currentWorkspaceName = workspaces.find((w) => w.is_current || w.company_id === user?.company_id)?.name || "Workspace";

  const initials = (user?.name || user?.email || "U")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition ${isActive ? "bg-[#2563EB] text-white" : "text-slate-700 hover:bg-slate-100"}`;

  const renderNav = (close = () => {}) => (
    <>
      <div className="h-16 flex items-center px-5 border-b border-slate-200 shrink-0">
        <img src={BRAND_LOGO} alt={APP_NAME} className="h-7 w-auto object-contain" />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-0.5">
        <div className="px-3 pb-2">
          <p className="overline">Workspace</p>
          <p className="text-sm font-semibold text-slate-900 truncate mt-0.5" title={currentWorkspaceName} data-testid="sidebar-workspace-name">{currentWorkspaceName}</p>
        </div>
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink key={item.path} to={item.path} end={item.end} onClick={close}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`} className={navLinkClass}>
              <Icon className="h-4 w-4" />
              {item.label}
              {item.path === "/inbox" && unread > 0 && <span className="ml-auto text-[10px] bg-blue-600 text-white rounded-full px-1.5 py-0.5" data-testid="nav-inbox-unread-badge">{unread}</span>}
            </NavLink>
          );
        })}
        {user?.role === "admin" && (
          <>
            <p className="overline px-3 pt-5 pb-2">Administration</p>
            {ADMIN_NAV.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink key={item.path} to={item.path} onClick={close} data-testid={`nav-admin-${item.path.split("/").pop()}`} className={navLinkClass}>
                  <Icon className="h-4 w-4" />{item.label}
                </NavLink>
              );
            })}
          </>
        )}
        {user?.platform_admin && (
          <>
            <p className="overline px-3 pt-5 pb-2">Platform</p>
            <NavLink to="/platform" onClick={close} data-testid="nav-platform-admin" className={navLinkClass}>
              <ShieldCheck className="h-4 w-4" />Workspace Billing
            </NavLink>
          </>
        )}
      </nav>
      <div className="p-4 border-t border-slate-200 shrink-0">
        <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
          <p className="overline">Signed in</p>
          <p className="mt-1 text-sm font-medium text-slate-900 truncate">{user?.name}</p>
          <p className="text-xs text-slate-500 truncate">{user?.email}</p>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-100 flex" data-testid="app-layout">
      <LegalConsentGate />
      <WorkspaceSetupGate />
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-slate-200 bg-white">
        {renderNav()}
      </aside>

      {/* Mobile drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-72 p-0 flex flex-col lg:hidden" data-testid="mobile-nav-drawer">
          <SheetTitle className="sr-only">Navigation menu</SheetTitle>
          {renderNav(() => setDrawerOpen(false))}
        </SheetContent>
      </Sheet>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-16 flex items-center gap-3 px-4 sm:px-8 border-b border-slate-200 bg-white/80 backdrop-blur-xl sticky top-0 z-20">
          <button onClick={() => setDrawerOpen(true)} className="lg:hidden h-9 w-9 grid place-items-center rounded-md hover:bg-slate-100 transition shrink-0" data-testid="mobile-menu-btn" aria-label="Open menu">
            <Menu className="h-5 w-5 text-slate-700" />
          </button>
          <img src={BRAND_LOGO} alt={APP_NAME} className="lg:hidden h-6 w-auto object-contain shrink-0" />
          <GlobalSearch />

          <button onClick={() => navigate("/inbox")} className="relative h-9 w-9 grid place-items-center rounded-md hover:bg-slate-100 transition" data-testid="topbar-notifications-btn">
            <Bell className="h-4 w-4 text-slate-600" />
            {unread > 0 && <span className="absolute top-2 right-2 h-1.5 w-1.5 rounded-full bg-blue-600" />}
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 pl-2 pr-2.5 py-1.5 rounded-md hover:bg-slate-100 transition" data-testid="topbar-profile-btn">
                <Avatar className="h-7 w-7">
                  <AvatarImage src={avatarUrl(user?.picture)} alt={user?.name} />
                  <AvatarFallback className="text-xs bg-slate-900 text-white">{initials}</AvatarFallback>
                </Avatar>
                <div className="hidden sm:block text-left">
                  <div className="text-xs font-medium text-slate-900 leading-tight">{user?.name}</div>
                  <div className="text-[10px] text-slate-500 leading-tight capitalize">{user?.role?.replace("_", " ")}</div>
                </div>
                <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-medium">{user?.name}</div>
                <div className="text-xs text-slate-500 font-normal">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/account")} data-testid="menu-my-account">
                <Users className="h-4 w-4 mr-2" />
                My Account
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/settings/notifications")} data-testid="menu-notification-settings">
                <Bell className="h-4 w-4 mr-2" />
                Email notifications
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/contact")} data-testid="menu-contact-feedback">
                <LifeBuoy className="h-4 w-4 mr-2" />
                {user?.language === "it" ? "Contatti & Feedback" : "Contact & Feedback"}
              </DropdownMenuItem>
              {user?.role === "admin" && (
                <DropdownMenuItem onClick={() => navigate("/admin/settings")} data-testid="menu-workspace-settings">
                  <Settings className="h-4 w-4 mr-2" />
                  Workspace Settings
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger data-testid="menu-switch-workspace">
                  <Building2 className="h-4 w-4 mr-2" />
                  Switch Workspace
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-56">
                  {workspaces.map((w) => (
                    <DropdownMenuItem key={w.company_id} onClick={() => switchWorkspace(w.company_id)} data-testid={`switch-ws-${w.company_id}`}>
                      <span className="truncate">{w.name}</span>
                      {w.is_current && <Badge variant="outline" className="ml-auto text-[10px]">Current</Badge>}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setCreateWsOpen(true)} data-testid="menu-create-workspace">
                    <Zap className="h-4 w-4 mr-2" />Create workspace
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-700" data-testid="menu-logout-btn">
                <LogOut className="h-4 w-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
      <CreateWorkspaceDialog open={createWsOpen} onOpenChange={setCreateWsOpen} onCreated={switchWorkspace} />
    </div>
  );
}
